import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  WorkflowInstance,
  WorkflowStep,
  WorkflowStepState,
  WorkflowState,
  WorkflowDecision
} from '../../models/file-vault.models';

export type WorkflowHistoryViewMode = 'timeline' | 'grid';

export interface WorkflowHistoryEntry {
  stepIndex: number;
  stepName: string;
  state: WorkflowStepState;
  decision?: WorkflowDecision;
  assignedTo?: string;
  assignedToRole?: string;
  completedBy?: string;
  completedAt?: Date;
  dueDate?: Date;
  comments?: string;
  delegatedTo?: string;
  delegatedAt?: Date;
  isCurrentStep: boolean;
}

@Component({
  selector: 'fv-workflow-history',
  standalone: true,
  template: `
    <div class="workflow-history">
      <!-- Header -->
      <div class="header">
        <div class="header-left">
          <h3 class="title">Workflow History</h3>
          <span class="workflow-name" *ngIf="workflowName">{{ workflowName }}</span>
        </div>
        <div class="header-right">
          <div class="view-toggle">
            <button kendoButton
                    [look]="viewMode === 'timeline' ? 'default' : 'flat'"
                    (click)="setViewMode('timeline')"
                    [themeColor]="viewMode === 'timeline' ? 'primary' : 'base'">
              Timeline
            </button>
            <button kendoButton
                    [look]="viewMode === 'grid' ? 'default' : 'flat'"
                    (click)="setViewMode('grid')"
                    [themeColor]="viewMode === 'grid' ? 'primary' : 'base'">
              Grid
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <span class="loading-spinner"></span>
        Loading history...
      </div>

      <!-- Timeline View -->
      <div class="timeline-view" *ngIf="!isLoading && viewMode === 'timeline'">
        <div class="timeline">
          <div class="timeline-item"
               *ngFor="let entry of historyEntries; let i = index"
               [ngClass]="getTimelineItemClass(entry)">
            <div class="timeline-marker">
              <div class="marker-dot">
                <span class="marker-icon">{{ getStepIcon(entry.state) }}</span>
              </div>
              <div class="marker-line" *ngIf="i < historyEntries.length - 1"></div>
            </div>
            <div class="timeline-content">
              <div class="timeline-header">
                <span class="step-number">Step {{ entry.stepIndex + 1 }}</span>
                <span class="step-name">{{ entry.stepName }}</span>
                <span class="state-badge" [ngClass]="getStateBadgeClass(entry.state)">
                  {{ getStateLabel(entry.state) }}
                </span>
              </div>
              <div class="timeline-body">
                <div class="timeline-row" *ngIf="entry.completedAt">
                  <span class="timeline-label">Completed:</span>
                  <span class="timeline-value">
                    {{ entry.completedAt | date:'medium' }}
                  </span>
                </div>
                <div class="timeline-row" *ngIf="entry.completedBy">
                  <span class="timeline-label">By:</span>
                  <span class="timeline-value">{{ entry.completedBy }}</span>
                </div>
                <div class="timeline-row" *ngIf="entry.decision !== undefined">
                  <span class="timeline-label">Decision:</span>
                  <span class="timeline-value decision" [ngClass]="getDecisionClass(entry.decision)">
                    {{ getDecisionLabel(entry.decision) }}
                  </span>
                </div>
                <div class="timeline-row" *ngIf="entry.delegatedTo">
                  <span class="timeline-label">Delegated to:</span>
                  <span class="timeline-value">{{ entry.delegatedTo }}</span>
                </div>
                <div class="timeline-row" *ngIf="entry.assignedTo || entry.assignedToRole">
                  <span class="timeline-label">Assigned:</span>
                  <span class="timeline-value">
                    {{ entry.assignedTo || entry.assignedToRole }}
                  </span>
                </div>
                <div class="timeline-row" *ngIf="entry.dueDate && entry.state === WorkflowStepState.Pending">
                  <span class="timeline-label">Due:</span>
                  <span class="timeline-value" [ngClass]="{ 'overdue': isOverdue(entry) }">
                    {{ entry.dueDate | date:'shortDate' }}
                    <span class="overdue-tag" *ngIf="isOverdue(entry)">OVERDUE</span>
                  </span>
                </div>
                <div class="timeline-comments" *ngIf="entry.comments">
                  <span class="comments-label">Comments:</span>
                  <p class="comments-text">"{{ entry.comments }}"</p>
                </div>
              </div>
              <div class="current-badge" *ngIf="entry.isCurrentStep">
                CURRENT STEP
              </div>
            </div>
          </div>
        </div>
        <div class="empty-state" *ngIf="historyEntries.length === 0">
          <p>No workflow history available</p>
        </div>
      </div>

      <!-- Grid View -->
      <div class="grid-view" *ngIf="!isLoading && viewMode === 'grid'">
        <kendo-grid
          [data]="gridData"
          [sortable]="true"
          class="history-grid">

          <kendo-grid-column field="stepIndex" title="#" [width]="50">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ dataItem.stepIndex + 1 }}
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column field="stepName" title="Step Name" [width]="180">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span [ngClass]="{ 'current-step': dataItem.isCurrentStep }">
                {{ dataItem.stepName }}
              </span>
              <span class="current-indicator" *ngIf="dataItem.isCurrentStep">(Current)</span>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column field="state" title="State" [width]="110">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span class="state-badge small" [ngClass]="getStateBadgeClass(dataItem.state)">
                {{ getStateLabel(dataItem.state) }}
              </span>
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column field="assignedTo" title="Assigned To" [width]="140">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ dataItem.assignedTo || dataItem.assignedToRole || '-' }}
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column field="completedBy" title="Completed By" [width]="140">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ dataItem.completedBy || '-' }}
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column field="completedAt" title="Completed" [width]="150">
            <ng-template kendoGridCellTemplate let-dataItem>
              {{ dataItem.completedAt ? (dataItem.completedAt | date:'short') : '-' }}
            </ng-template>
          </kendo-grid-column>

          <kendo-grid-column field="comments" title="Comments" [width]="200">
            <ng-template kendoGridCellTemplate let-dataItem>
              <span class="comments-cell" [title]="dataItem.comments || ''">
                {{ dataItem.comments || '-' }}
              </span>
            </ng-template>
          </kendo-grid-column>
        </kendo-grid>
      </div>

      <!-- Summary -->
      <div class="summary" *ngIf="!isLoading && workflow">
        <div class="summary-item">
          <span class="summary-label">Started:</span>
          <span class="summary-value">{{ workflow.startedAt | date:'medium' }}</span>
          <span class="summary-by" *ngIf="workflow.startedBy">by {{ workflow.startedBy }}</span>
        </div>
        <div class="summary-item" *ngIf="workflow.completedAt">
          <span class="summary-label">Completed:</span>
          <span class="summary-value">{{ workflow.completedAt | date:'medium' }}</span>
        </div>
        <div class="summary-item">
          <span class="summary-label">Status:</span>
          <span class="workflow-state-badge" [ngClass]="getWorkflowStateClass()">
            {{ getWorkflowStateLabel() }}
          </span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .workflow-history {
      padding: 16px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 12px;
    }

    .header-left {
      display: flex;
      align-items: baseline;
      gap: 12px;
    }

    .title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .workflow-name {
      font-size: 14px;
      color: var(--fv-text-secondary);
    }

    .view-toggle {
      display: flex;
      gap: 4px;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .loading-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid var(--fv-border);
      border-top-color: var(--fv-text-accent);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Timeline View */
    .timeline {
      position: relative;
      padding-left: 8px;
    }

    .timeline-item {
      display: flex;
      gap: 16px;
      margin-bottom: 8px;
    }

    .timeline-marker {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 40px;
      flex-shrink: 0;
    }

    .marker-dot {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: var(--fv-border);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid var(--fv-border);
      z-index: 1;
    }

    .timeline-item.approved .marker-dot {
      background: var(--fv-status-success-bg);
      border-color: #4caf50;
    }

    .timeline-item.rejected .marker-dot {
      background: var(--fv-status-error-bg);
      border-color: #f44336;
    }

    .timeline-item.skipped .marker-dot {
      background: var(--fv-status-warning-bg);
      border-color: #ff9800;
    }

    .timeline-item.delegated .marker-dot {
      background: var(--fv-status-purple-bg);
      border-color: #9c27b0;
    }

    .timeline-item.current .marker-dot {
      background: var(--fv-status-info-bg);
      border-color: var(--fv-text-accent);
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4); }
      50% { box-shadow: 0 0 0 6px rgba(25, 118, 210, 0); }
    }

    .marker-icon {
      font-size: 16px;
      font-weight: bold;
    }

    .timeline-item.approved .marker-icon { color: var(--fv-status-success-fg); }
    .timeline-item.rejected .marker-icon { color: var(--fv-status-error-fg); }
    .timeline-item.skipped .marker-icon { color: var(--fv-status-warning-fg); }
    .timeline-item.delegated .marker-icon { color: var(--fv-status-purple-fg); }
    .timeline-item.current .marker-icon { color: var(--fv-text-accent); }
    .timeline-item.pending .marker-icon { color: var(--fv-text-muted); }

    .marker-line {
      flex: 1;
      width: 2px;
      background: var(--fv-border);
      min-height: 20px;
    }

    .timeline-item.approved .marker-line,
    .timeline-item.skipped .marker-line,
    .timeline-item.delegated .marker-line {
      background: #4caf50;
    }

    .timeline-content {
      flex: 1;
      background: var(--fv-surface-alt);
      border-radius: 8px;
      padding: 12px 16px;
      position: relative;
      margin-bottom: 8px;
    }

    .timeline-item.current .timeline-content {
      background: var(--fv-status-info-bg);
      border: 1px solid var(--fv-text-accent);
    }

    .timeline-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
      flex-wrap: wrap;
    }

    .step-number {
      font-size: 11px;
      color: var(--fv-text-muted);
      font-weight: 500;
    }

    .step-name {
      font-weight: 600;
      color: var(--fv-text);
    }

    .state-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .state-badge.small {
      padding: 2px 6px;
      font-size: 9px;
    }

    .state-badge.pending { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .state-badge.in-progress { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .state-badge.skipped { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .state-badge.delegated { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }

    .timeline-body {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .timeline-row {
      display: flex;
      gap: 8px;
      font-size: 13px;
    }

    .timeline-label {
      color: var(--fv-text-muted);
      min-width: 80px;
    }

    .timeline-value {
      color: var(--fv-text);
    }

    .timeline-value.decision {
      font-weight: 500;
    }

    .timeline-value.decision.approve { color: var(--fv-status-success-fg); }
    .timeline-value.decision.reject { color: var(--fv-status-error-fg); }
    .timeline-value.decision.delegate { color: var(--fv-status-purple-fg); }

    .overdue {
      color: var(--fv-status-error-fg);
    }

    .overdue-tag {
      display: inline-block;
      margin-left: 6px;
      padding: 1px 4px;
      background: var(--fv-status-error-fg);
      color: white;
      border-radius: 2px;
      font-size: 9px;
      font-weight: 600;
    }

    .timeline-comments {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid var(--fv-border);
    }

    .comments-label {
      font-size: 11px;
      color: var(--fv-text-muted);
      display: block;
      margin-bottom: 4px;
    }

    .comments-text {
      margin: 0;
      font-style: italic;
      color: var(--fv-text);
      font-size: 13px;
    }

    .current-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      padding: 2px 8px;
      background: var(--fv-text-accent);
      color: white;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
    }

    .empty-state {
      padding: 48px;
      text-align: center;
      color: var(--fv-text-muted);
    }

    /* Grid View */
    .history-grid {
      font-size: 13px;
    }

    .current-step {
      font-weight: 600;
    }

    .current-indicator {
      margin-left: 4px;
      font-size: 11px;
      color: var(--fv-text-accent);
      font-weight: normal;
    }

    .comments-cell {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 180px;
    }

    /* Summary */
    .summary {
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid var(--fv-border);
      display: flex;
      flex-wrap: wrap;
      gap: 24px;
    }

    .summary-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
    }

    .summary-label {
      color: var(--fv-text-muted);
    }

    .summary-value {
      color: var(--fv-text);
    }

    .summary-by {
      color: var(--fv-text-secondary);
      font-size: 12px;
    }

    .workflow-state-badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
    }

    .workflow-state-badge.pending { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .workflow-state-badge.in-progress { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .workflow-state-badge.completed { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .workflow-state-badge.cancelled { background: var(--fv-surface-alt); color: var(--fv-text-muted); }
    .workflow-state-badge.failed { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
  `],
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule]
})
export class WorkflowHistoryComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  // Expose enum for template
  WorkflowStepState = WorkflowStepState;

  /** Workflow instance to display */
  @Input() workflow?: WorkflowInstance;

  /** Alternatively, provide workflow instance ID to load */
  @Input() workflowId?: string;

  /** Initial view mode */
  @Input() viewMode: WorkflowHistoryViewMode = 'timeline';

  /** Emitted when view mode changes */
  @Output() viewModeChange = new EventEmitter<WorkflowHistoryViewMode>();

  historyEntries: WorkflowHistoryEntry[] = [];
  gridData: GridDataResult = { data: [], total: 0 };
  isLoading = false;

  ngOnInit(): void {
    this.loadWorkflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflowId'] && this.workflowId) {
      this.loadWorkflow();
    }
    if (changes['workflow'] && this.workflow) {
      this.buildHistoryEntries();
    }
  }

  async loadWorkflow(): Promise<void> {
    if (this.workflow) {
      this.buildHistoryEntries();
      return;
    }

    if (!this.workflowId) return;

    this.isLoading = true;
    try {
      this.workflow = await this.api.getWorkflow(this.workflowId, true).toPromise();
      this.buildHistoryEntries();
    } catch (error) {
      console.error('Failed to load workflow', error);
    } finally {
      this.isLoading = false;
    }
  }

  buildHistoryEntries(): void {
    if (!this.workflow?.steps) {
      this.historyEntries = [];
      this.gridData = { data: [], total: 0 };
      return;
    }

    const currentIndex = this.workflow.currentStepIndex ?? 0;

    this.historyEntries = this.workflow.steps
      .map((step): WorkflowHistoryEntry => ({
        stepIndex: step.stepIndex,
        stepName: step.stepName,
        state: step.state,
        decision: step.decision,
        assignedTo: step.assignedTo,
        assignedToRole: step.assignedToRole,
        completedBy: step.completedBy,
        completedAt: step.completedAt ? new Date(step.completedAt) : undefined,
        dueDate: step.dueDate ? new Date(step.dueDate) : undefined,
        comments: step.comments,
        delegatedTo: step.delegatedTo,
        delegatedAt: step.delegatedAt ? new Date(step.delegatedAt) : undefined,
        isCurrentStep: step.stepIndex === currentIndex &&
                       (step.state === WorkflowStepState.Pending || step.state === WorkflowStepState.InProgress)
      }))
      .sort((a, b) => a.stepIndex - b.stepIndex);

    this.gridData = {
      data: this.historyEntries,
      total: this.historyEntries.length
    };
  }

  setViewMode(mode: WorkflowHistoryViewMode): void {
    this.viewMode = mode;
    this.viewModeChange.emit(mode);
  }

  get workflowName(): string {
    return this.workflow?.templateName ?? this.workflow?.template?.name ?? '';
  }

  // Helper methods
  getTimelineItemClass(entry: WorkflowHistoryEntry): string {
    const classes: string[] = [];

    switch (entry.state) {
      case WorkflowStepState.Approved:
        classes.push('approved');
        break;
      case WorkflowStepState.Rejected:
        classes.push('rejected');
        break;
      case WorkflowStepState.Skipped:
        classes.push('skipped');
        break;
      case WorkflowStepState.Delegated:
        classes.push('delegated');
        break;
      case WorkflowStepState.Pending:
        classes.push('pending');
        break;
      case WorkflowStepState.InProgress:
        classes.push('current');
        break;
    }

    if (entry.isCurrentStep) {
      classes.push('current');
    }

    return classes.join(' ');
  }

  getStepIcon(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Approved: return '\u2713';
      case WorkflowStepState.Rejected: return '\u2717';
      case WorkflowStepState.Skipped: return '\u21B7';
      case WorkflowStepState.Delegated: return '\u21C4';
      case WorkflowStepState.InProgress: return '\u25B6';
      case WorkflowStepState.Pending: return '\u25CB';
      default: return '\u25CB';
    }
  }

  getStateLabel(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Pending: return 'Pending';
      case WorkflowStepState.InProgress: return 'In Progress';
      case WorkflowStepState.Approved: return 'Approved';
      case WorkflowStepState.Rejected: return 'Rejected';
      case WorkflowStepState.Skipped: return 'Skipped';
      case WorkflowStepState.Delegated: return 'Delegated';
      default: return 'Unknown';
    }
  }

  getStateBadgeClass(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Pending: return 'pending';
      case WorkflowStepState.InProgress: return 'in-progress';
      case WorkflowStepState.Approved: return 'approved';
      case WorkflowStepState.Rejected: return 'rejected';
      case WorkflowStepState.Skipped: return 'skipped';
      case WorkflowStepState.Delegated: return 'delegated';
      default: return '';
    }
  }

  getDecisionLabel(decision: WorkflowDecision | undefined): string {
    if (decision === undefined) return '';
    switch (decision) {
      case WorkflowDecision.Approve: return 'Approved';
      case WorkflowDecision.Reject: return 'Rejected';
      case WorkflowDecision.RequestChanges: return 'Requested Changes';
      case WorkflowDecision.Delegate: return 'Delegated';
      default: return 'Unknown';
    }
  }

  getDecisionClass(decision: WorkflowDecision | undefined): string {
    if (decision === undefined) return '';
    switch (decision) {
      case WorkflowDecision.Approve: return 'approve';
      case WorkflowDecision.Reject: return 'reject';
      case WorkflowDecision.Delegate: return 'delegate';
      default: return '';
    }
  }

  getWorkflowStateLabel(): string {
    if (!this.workflow) return '';
    switch (this.workflow.state) {
      case WorkflowState.Pending: return 'Pending';
      case WorkflowState.InProgress: return 'In Progress';
      case WorkflowState.Completed: return 'Completed';
      case WorkflowState.Cancelled: return 'Cancelled';
      case WorkflowState.Failed: return 'Failed';
      default: return 'Unknown';
    }
  }

  getWorkflowStateClass(): string {
    if (!this.workflow) return '';
    switch (this.workflow.state) {
      case WorkflowState.Pending: return 'pending';
      case WorkflowState.InProgress: return 'in-progress';
      case WorkflowState.Completed: return 'completed';
      case WorkflowState.Cancelled: return 'cancelled';
      case WorkflowState.Failed: return 'failed';
      default: return '';
    }
  }

  isOverdue(entry: WorkflowHistoryEntry): boolean {
    if (!entry.dueDate) return false;
    if (entry.state !== WorkflowStepState.Pending && entry.state !== WorkflowStepState.InProgress) {
      return false;
    }
    return entry.dueDate < new Date();
  }
}
